import type { TuningIdentity, TuningSettingReceipt } from '../contracts/identity';
import { isTuningPatch } from '../contracts/settings';
import type { TuningPatch, TuningSettingKey, TuningValue } from '../contracts/settings';
import type { HostEncodingReadback, HostEncodingResult, HostSenderWriter, IHostEncodingAuthority } from './HostEncodingContracts';
import { HostResolutionScaling } from './HostResolutionScaling';

type Scope = { target: TuningIdentity; connection: RTCPeerConnection; patch: TuningPatch;
  baseline: Map<RTCRtpSender, RTCRtpSendParameters>; source?: { width?: number; height?: number }; active: boolean };
const liveKeys: readonly TuningSettingKey[] = ['video.maxBitrateBps', 'video.maxFramerate',
  'video.resolutionTier', 'video.degradationPreference', 'audio.maxBitrateBps'];

/** Scoped overrides decorate every native write, including delayed production intents. */
export class HostEncodingAuthority implements IHostEncodingAuthority {
  private readonly scopes = new Map<string, Scope>();
  private readonly senderScopes = new WeakMap<RTCRtpSender, Scope>();
  private readonly scaling = new HostResolutionScaling();
  constructor(private readonly write: HostSenderWriter) {}

  public attach(target: TuningIdentity, connection: RTCPeerConnection): void {
    const previous = this.scopes.get(target.peerId);
    if (previous?.active && (target.connectionEpoch < previous.target.connectionEpoch ||
      (target.connectionEpoch === previous.target.connectionEpoch && target.configRevision < previous.target.configRevision))) {
      throw new Error('stale-tuning-generation');
    }
    if (previous && (previous.connection !== connection || previous.target.connectionEpoch !== target.connectionEpoch)) {
      previous.active = false;
    }
    if (previous?.active && previous.connection === connection) {
      previous.target = { ...target };
      const track = connection.getSenders().find(sender => sender.track?.kind === 'video')?.track;
      if (track?.getSettings) previous.source = track.getSettings();
      return;
    }
    const scope: Scope = { target: { ...target }, connection, patch: {}, baseline: new Map(), active: true };
    this.scopes.set(target.peerId, scope);
    for (const sender of connection.getSenders()) {
      this.senderScopes.set(sender, scope);
      scope.baseline.set(sender, structuredClone(sender.getParameters() as RTCRtpSendParameters));
      if (sender.track?.kind === 'video') scope.source = sender.track.getSettings?.();
    }
  }

  public decorate(sender: RTCRtpSender, parameters: RTCRtpSendParameters): void {
    const scope = this.senderScopes.get(sender);
    if (!scope?.active || !parameters.encodings?.length) return;
    const encoding = parameters.encodings[0];
    const patch = scope.patch;
    if (sender.track?.kind === 'audio') {
      if (typeof patch['audio.maxBitrateBps'] === 'number') encoding.maxBitrate = patch['audio.maxBitrateBps'];
      return;
    }
    if (typeof patch['video.maxBitrateBps'] === 'number') encoding.maxBitrate = patch['video.maxBitrateBps'];
    if (typeof patch['video.maxFramerate'] === 'number') encoding.maxFramerate = patch['video.maxFramerate'];
    if (typeof patch['video.degradationPreference'] === 'string') {
      parameters.degradationPreference = patch['video.degradationPreference'] as RTCDegradationPreference;
    }
    const tier = patch['video.resolutionTier'];
    if (typeof tier === 'string') {
      const scale = this.scaling.scale(tier, scope.source?.width, scope.source?.height);
      if (scale !== undefined) encoding.scaleResolutionDownBy = scale;
    }
  }

  public async apply(target: TuningIdentity, patch: TuningPatch): Promise<HostEncodingResult> {
    const scope = this.requireScope(target);
    if (!isTuningPatch(patch)) throw new Error('invalid-tuning-patch');
    const settings: TuningSettingReceipt[] = [];
    const applicable: Partial<Record<TuningSettingKey, TuningValue>> = {};
    for (const [key, requested] of Object.entries(patch) as [TuningSettingKey, TuningValue][]) {
      if (!liveKeys.includes(key)) {
        settings.push({ key, requested, status: 'unsupported', reason: 'Not a live sender setting' });
      } else if (key === 'video.resolutionTier' && this.scaling.scale(String(requested),
        scope.source?.width, scope.source?.height) === undefined) {
        settings.push({ key, requested, status: 'unsupported', reason: 'Source dimensions unavailable' });
      } else applicable[key] = requested;
    }
    const previous = scope.patch;
    const senders = scope.connection.getSenders().filter(sender => sender.track?.kind === 'video'
      ? Object.keys(applicable).some(key => key.startsWith('video.'))
      : sender.track?.kind === 'audio' && applicable['audio.maxBitrateBps'] !== undefined);
    const nativeBefore = new Map<RTCRtpSender, RTCRtpSendParameters>();
    const attempted = new Set<RTCRtpSender>();
    for (const sender of senders) nativeBefore.set(sender, structuredClone(sender.getParameters() as RTCRtpSendParameters));
    if (Object.keys(applicable).length && !senders.length) throw new Error('sender-unavailable');
    scope.patch = { ...previous, ...applicable };
    try {
      await Promise.all(senders.map(sender =>
        this.write(sender, async () => {
          this.requireScope(target);
          const parameters = sender.getParameters() as RTCRtpSendParameters;
          if (!parameters.encodings?.length) throw new Error('sender-encodings-unavailable');
          this.decorate(sender, parameters);
          attempted.add(sender);
          await sender.setParameters(parameters);
          this.requireScope(target);
        })));
      const readback = this.readback(target);
      for (const [key, requested] of Object.entries(applicable) as [TuningSettingKey, TuningValue][]) {
        const actual = this.valueFor(key, readback);
        const expected = key === 'video.resolutionTier'
          ? this.scaling.scale(String(requested), scope.source?.width, scope.source?.height) : requested;
        settings.push({ key, requested, accepted: requested, ...(actual === undefined ? {} : { readback: actual }),
          status: actual === undefined ? 'unsupported' : actual === expected ? 'applied' : 'clamped',
          ...(actual === undefined ? { reason: 'Native readback unavailable' } : {}) });
      }
      return { target, readback, settings };
    } catch (error) {
      if (scope.active) {
        scope.patch = previous;
        const rollback = await Promise.allSettled([...nativeBefore].filter(([sender]) => attempted.has(sender)).map(([sender, before]) => this.write(sender, async () => {
          this.requireScope(target);
          const fresh = sender.getParameters() as RTCRtpSendParameters;
          if (!fresh.encodings?.length || !before.encodings?.length) throw new Error('rollback-encodings-unavailable');
          fresh.encodings[0] = { ...before.encodings[0] };
          fresh.degradationPreference = before.degradationPreference;
          await sender.setParameters(fresh);
        })));
        if (rollback.some(result => result.status === 'rejected')) throw new Error('sender-rollback-failed');
      }
      throw error;
    }
  }

  public observeSource(target: TuningIdentity, source: {
    sourceId: string; captureGeneration: number; width?: number; height?: number; fps?: number;
  }): void { this.requireScope(target).source = source; }

  public readback(target: TuningIdentity): HostEncodingReadback {
    const scope = this.requireScope(target);
    const result: HostEncodingReadback = {};
    for (const sender of scope.connection.getSenders()) {
      const params = sender.getParameters() as RTCRtpSendParameters; const encoding = params.encodings?.[0];
      if (!encoding) continue;
      if (sender.track?.kind === 'audio') { result.audioMaxBitrateBps = encoding.maxBitrate; continue; }
      result.maxBitrateBps = encoding.maxBitrate; result.maxFramerate = encoding.maxFramerate;
      result.scaleResolutionDownBy = encoding.scaleResolutionDownBy;
      result.degradationPreference = params.degradationPreference;
      result.scalabilityMode = (encoding as { scalabilityMode?: string }).scalabilityMode;
      const codec = (encoding as { codec?: RTCRtpCodec }).codec;
      result.selectedCodec = codec?.mimeType; result.codecProfile = codec?.sdpFmtpLine;
    }
    return result;
  }

  public async release(target: TuningIdentity): Promise<void> {
    const scope = this.requireScope(target); scope.patch = {};
    await Promise.all([...scope.baseline].map(([sender, baseline]) => this.write(sender, async () => {
      this.requireScope(target);
      const parameters = sender.getParameters() as RTCRtpSendParameters; const original = baseline.encodings?.[0];
      if (!original) return;
      if (!parameters.encodings?.length) throw new Error('restoration-encodings-unavailable');
      for (const key of ['maxBitrate', 'maxFramerate', 'scaleResolutionDownBy'] as const) {
        if (original[key] === undefined) delete parameters.encodings[0][key];
        else parameters.encodings[0][key] = original[key];
      }
      parameters.degradationPreference = baseline.degradationPreference;
      await sender.setParameters(parameters);
    })));
    scope.active = false; this.scopes.delete(target.peerId);
  }

  public invalidate(connection: RTCPeerConnection): void {
    for (const [peerId, scope] of this.scopes) if (scope.connection === connection) {
      scope.active = false; this.scopes.delete(peerId);
    }
  }

  private requireScope(target: TuningIdentity): Scope {
    const scope = this.scopes.get(target.peerId);
    if (!scope?.active || scope.target.sessionId !== target.sessionId || scope.target.trialId !== target.trialId
      || scope.target.connectionEpoch !== target.connectionEpoch || scope.target.configRevision !== target.configRevision) {
      throw new Error('stale-tuning-generation');
    }
    return scope;
  }
  private valueFor(key: TuningSettingKey, readback: HostEncodingReadback): TuningValue | undefined {
    switch (key) {
      case 'video.maxBitrateBps': return readback.maxBitrateBps;
      case 'video.maxFramerate': return readback.maxFramerate;
      case 'video.resolutionTier': return readback.scaleResolutionDownBy;
      case 'video.degradationPreference': return readback.degradationPreference;
      case 'audio.maxBitrateBps': return readback.audioMaxBitrateBps;
      default: return undefined;
    }
  }
}
